import { useSyncExternalStore } from 'react';

export interface Nudge {
  readonly id: string;
  readonly title: string;
  readonly summary: string;
  readonly conversationId: string;
  readonly agentId: string;
  /** Task that produced this nudge — lets the user mute it (OMNI-757). */
  readonly taskId: string;
}

interface NudgeStoreState {
  nudges: Nudge[];
  // Ids the user has already dismissed/opened this session. The poller keeps
  // returning a message until the backend PATCH lands; this guard stops it from
  // re-appearing in the meantime (and even if the PATCH fails).
  handled: Set<string>;
  listeners: Set<() => void>;
}

// Back the store with a single object on globalThis. Under the test runner the
// module can be evaluated more than once (separate instances for the test file
// and the component-under-test); a module-level `let` would then give each its
// own state and the component would never see what the test added. A global
// singleton makes every instance share one store. In the app there is only ever
// one instance, so this is purely defensive.
const KEY = '__cowbellNudgeStore__';
const g = globalThis as unknown as Record<string, NudgeStoreState | undefined>;
const store: NudgeStoreState = (g[KEY] ??= {
  nudges: [],
  handled: new Set(),
  listeners: new Set(),
});

function emit() {
  store.listeners.forEach((l) => l());
}

export function addNudge(n: Nudge): void {
  if (store.handled.has(n.id)) return; // user already dismissed/opened this one
  if (store.nudges.some((x) => x.id === n.id)) return; // dedupe by message id
  // oldest-first: first stays on top, new appended below
  store.nudges = [...store.nudges, n];
  emit();
}

/** Record that the user acted on a nudge so the poller won't re-add it before
 *  the backend status update lands. See `addNudge`. */
export function markHandled(id: string): void {
  store.handled.add(id);
}

export function removeNudge(id: string): void {
  const next = store.nudges.filter((x) => x.id !== id);
  if (next.length !== store.nudges.length) {
    store.nudges = next;
    emit();
  }
}

export function getNudges(): Nudge[] {
  return store.nudges;
}

/**
 * Drop all active nudges. Called on logout (see UserActions.clearClientState)
 * so a different user signing in on the same tab never sees the previous
 * session's proactive summaries or its conversation ids (OMNI-480).
 */
export function clearNudges(): void {
  store.handled.clear();
  if (store.nudges.length === 0) return;
  store.nudges = [];
  emit();
}

/** Drop visible nudges but keep the handled-guard — used on entity navigation
 *  (cards are entity-scoped) so a dismissed message can't flash back. */
export function clearVisibleNudges(): void {
  if (store.nudges.length === 0) return;
  store.nudges = [];
  emit();
}

export function subscribe(cb: () => void): () => void {
  store.listeners.add(cb);
  return () => {
    store.listeners.delete(cb);
  };
}

export function useNudges(): Nudge[] {
  return useSyncExternalStore(subscribe, getNudges, getNudges);
}
